import { api } from "../httpClient";
import { API_ENDPOINTS } from "../config";

// Cache for location suggestions to prevent duplicate API calls
const locationCache = new Map();
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

/**
 * Get location suggestions based on query
 * @param {string} query - Search query
 * @returns {Promise<Array>} Array of location objects
 */
export const getLocationSuggestions = async (query) => {
  try {
    if (!query || query.trim().length === 0) {
      return [];
    }

    const normalizedQuery = query.toLowerCase().trim();
    
    // Check cache first
    const cacheKey = `suggestions_${normalizedQuery}`;
    const cached = locationCache.get(cacheKey);
    
    if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
      // console.log("Using cached location suggestions for:", query);
      return cached.data;
    }

    // console.log("Fetching location suggestions for:", query);
    // const response = await api.get(API_ENDPOINTS.LOCATIONS);
    const response = await api.get(API_ENDPOINTS.LOCATIONS, {
      params: { query: normalizedQuery }
    });

    const locations = Array.isArray(response) ? response : [];

    // Filter locations based on query
    const filteredLocations = locations.filter((loc) => matchesQuery(loc, normalizedQuery));
    
    // Cache the result
    locationCache.set(cacheKey, {
      data: filteredLocations,
      timestamp: Date.now()
    });

    return filteredLocations;
  } catch (error) {
    console.error("Error fetching location suggestions:", error);
    return [];
  }
};

/**
 * Check if location matches the search query
 * @param {Object} location - Location object
 * @param {string} query - Search query in lowercase
 * @returns {boolean} True if location matches query
 */
const matchesQuery = (location, query) => {
  const searchableFields = [
    location.name,
    location.city,
    location.full_name,
    location.gdsCode,
    location.locationId,
  ].filter(Boolean); // Remove null/undefined values

  return searchableFields.some((field) =>
    field.toLowerCase().includes(query)
  );
};

/**
 * Get location by GDS code
 * @param {string} gdsCode - GDS location code
 * @returns {Promise<Object|null>} Location object or null
 */
export const getLocationByGdsCode = async (gdsCode) => {
  try {
    const locations = await getLocationSuggestions(gdsCode);
    return locations.find((loc) => loc.gdsCode === gdsCode) || null;
  } catch (error) {
    console.error("Error fetching location by GDS code:", error);
    return null;
  }
};

export const detectUserCountry = async () => {
  try {
    const response = await api.get(API_ENDPOINTS.DETECT_IP);
    return response?.country_code || "";
  } catch (error) {
    console.error("Error detecting user country:", error);
    return "";
  }
};

/**
 * Get popular locations
 * @returns {Promise<Array>} Array of popular locations
 */
export const getPopularLocations = async (query) => {

  try {
    if (!query || query.trim().length === 0) {
      return [];
    }

    const normalizedQuery = query.toLowerCase().trim();
    const response = await api.get(API_ENDPOINTS.LOCATION_SEARCH, {
      params: { query: normalizedQuery }
    });
    const locations = Array.isArray(response) ? response : [];
    
    // Filter for popular locations
    return locations.filter((loc) => 
      loc.isPopular || loc.name?.includes("Airport") || loc.name?.includes("Center")
    ).slice(0, 10);
  } catch (error) {
    console.error("Error fetching popular locations:", error);
    return [];
  }
};

/**
 * Format location object to display string
 * @param {Object} location - Location object
 * @returns {string} Formatted location string
 */
export const formatLocation = (location) => {
  if (!location) return "";
  
  const parts = [
    location.name,
    location.gdsCode && `(${location.gdsCode})`,
    location.city,
    location.country,
  ].filter(Boolean);

  return parts.join(", ");
}; 